import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { BLOG_ARTICLE } from '@pflegia/ui/pflegia/constants/routes';
import EmojiText from '@pflegia/ui/shared/emoji-text/EmojiText';

const Hyperlink = dynamic(() => import('@pflegia/ui/icons/icon-hyperlink-new.svg').then(mod => mod.ReactComponent));

// Priority: TVÖD > AVR
const SALARY_NEGOTIATIONS_KEY = 'salaryNegotiations';
const LINK_DEFS: {
  keyword: string;
  href: string;
}[] = [
  { keyword: 'tvod', href: `${BLOG_ARTICLE}/tvod-pflege-tarifvertrag-fur-den-offentlichen-dienst-in-der-pflege` },
  { keyword: 'avr', href: `${BLOG_ARTICLE}/avr-caritas-alles-zur-einrichtung-gehalt-gehaltstabelle` },
];

const findLinkDef = (text: string) =>
  LINK_DEFS.find(def =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .includes(def.keyword),
  );

export interface SummaryItemProps {
  black?: boolean;
  className?: string;
  emoji: string;
  label: string;
  value: string | string[];
  summaryKey?: string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ black = false, className, emoji, label, value, summaryKey, ...rest }) => {
  const valuesArray = Array.isArray(value) ? value : value.split(',').map(val => val.trim());
  // If any item has some linkeable text, keep them as separate items to render links.
  // Otherwise keep the original comma-separated display as a single string.
  const hasLinkable = valuesArray.some(val => findLinkDef(val));
  const values = summaryKey === SALARY_NEGOTIATIONS_KEY && hasLinkable ? valuesArray : [valuesArray.join(', ')];

  return (
    <div className={className}>
      <EmojiText emoji={emoji} emojiAsText>
        {label}
      </EmojiText>

      {values.map(val => {
        const islink = summaryKey === SALARY_NEGOTIATIONS_KEY ? findLinkDef(val) : undefined;
        const item = (
          <span
            {...rest}
            className={clsx(
              'flex items-center font-medium wrap-break-word group-hover:underline',
              black ? 'text-default' : 'text-primary',
            )}>
            {val}
            {islink ? <Hyperlink className='ml-2 h-4 w-4' aria-hidden='true' /> : null}
          </span>
        );

        return islink ? (
          <Link href={islink.href} target='_blank' rel='noopener noreferrer' className='group' key={val} aria-label={`${val} Artikel`}>
            {item}
          </Link>
        ) : (
          <div key={val}>{item}</div>
        );
      })}
    </div>
  );
};

export default SummaryItem;
